import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { IoCloudUploadOutline } from 'react-icons/io5';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { HiCheckCircle } from 'react-icons/hi';
import DefaultModal from 'components/Modal/DefaultModal';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { RootState } from 'app/store';
import { importPole } from '../../../../basic/pole/pole.reducer';
import PoleApi from '../../../../basic/pole/pole.api';

export interface ImportHistoryItem {
    id: string;
    fileName: string;
    uploadedAt: string;
    totalRows: number;
    successRows: number;
    failedRows: number;
    uploadedBy: string;
    status: 'Completed' | 'Failed';
}

interface JobPrepExcelPanelProps {
    jobId?: string;
    userName?: string;
    onImportSuccess?: (importedData: any[]) => void;
}

const JobPrepExcelPanel: React.FC<JobPrepExcelPanelProps> = ({
    jobId,
    userName = 'RBU',
    onImportSuccess,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const [lastUploadInfo, setLastUploadInfo] = useState<{
        time: string;
        fileName: string;
        total: number;
        success: number;
        failed: number;
    }>({
        time: '-',
        fileName: '-',
        total: 0,
        success: 0,
        failed: 0,
    });

    const [importHistory, setImportHistory] = useState<ImportHistoryItem[]>([]);

    const user = useAppSelector((state: RootState) => state.user.userAuth);

    const fetchImportLog = async () => {
        if (!jobId || !user?.id) return;
        try {
            const res = await PoleApi.getImportLog(jobId, 0, 100, user.id);
            const data = res.data;
            const logArray = Array.isArray(data) ? data : data ? [data] : [];

            if (logArray.length > 0) {
                const latestLog = logArray[0];
                const successCount = latestLog.successStatus
                    ? parseInt(
                          latestLog.successStatus.match(/\d+/)?.[0] || '0',
                          10,
                      )
                    : 0;
                const failedCount = latestLog.failedStatus
                    ? parseInt(
                          latestLog.failedStatus.match(/\d+/)?.[0] || '0',
                          10,
                      )
                    : 0;

                setLastUploadInfo({
                    time: moment(latestLog.createdDate).format(
                        'D MMM YYYY, h:mm A',
                    ),
                    fileName: latestLog.fileName || '-',
                    total: successCount + failedCount,
                    success: successCount,
                    failed: failedCount,
                });

                const historyItems: ImportHistoryItem[] = logArray.map(
                    (log: any, index: number) => {
                        const sCount = log.successStatus
                            ? parseInt(
                                  log.successStatus.match(/\d+/)?.[0] || '0',
                                  10,
                              )
                            : 0;
                        const fCount = log.failedStatus
                            ? parseInt(
                                  log.failedStatus.match(/\d+/)?.[0] || '0',
                                  10,
                              )
                            : 0;
                        return {
                            id: log.id || index.toString(),
                            fileName: log.fileName || '-',
                            uploadedAt: moment(log.createdDate).format(
                                'D MMM YYYY, h:mm A',
                            ),
                            totalRows: sCount + fCount,
                            successRows: sCount,
                            failedRows: fCount,
                            uploadedBy: userName || 'RBU',
                            status: fCount > 0 ? 'Failed' : 'Completed',
                        };
                    },
                );

                setImportHistory(historyItems);
            }
        } catch (err) {
            console.error('Failed to fetch import log', err);
        }
    };

    useEffect(() => {
        fetchImportLog();
    }, [jobId, user?.id]);

    // Download formatted Excel template with standard Job Prep columns
    const handleDownloadTemplate = () => {
        try {
            const link = document.createElement('a');
            link.href = '/templates/Job_Prep_Pole_Import_Template.xlsx';
            link.download = 'Job_Prep_Pole_Import_Template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Failed to download template:', err);
            Swal.fire({
                icon: 'error',
                title: 'Download Failed',
                text: 'Could not download Excel template.',
            });
        }
    };

    const dispatch = useAppDispatch();

    // Process file upload
    const processExcelFile = (file: File) => {
        if (!jobId) {
            Swal.fire({
                icon: 'error',
                title: 'No Job ID',
                text: 'Job ID is missing.',
            });
            return;
        }

        if (
            !file.name.endsWith('.xlsx') &&
            !file.name.endsWith('.xls') &&
            !file.name.endsWith('.csv')
        ) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid File',
                text: 'Please upload an Excel file (.xlsx or .xls)',
            });
            return;
        }

        dispatch(importPole({ jobId, file }))
            .unwrap()
            .then(() => {
                Swal.fire({
                    icon: 'success',
                    title: 'File Uploaded',
                    text: `Successfully imported ${file.name}`,
                    timer: 2500,
                    showConfirmButton: false,
                });

                fetchImportLog();

                if (onImportSuccess) {
                    onImportSuccess([]);
                }
            })
            .catch((error) => {
                console.error('Error importing Excel file:', error);
                const serverErrorMessage =
                    typeof error?.data === 'string'
                        ? error.data
                        : error?.data?.message;
                const displayMessage =
                    serverErrorMessage ||
                    error?.message ||
                    'Failed to upload the Excel file.';

                Swal.fire({
                    icon: 'error',
                    title: 'Upload Error',
                    text: displayMessage,
                });
            });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            processExcelFile(e.target.files[0]);
            e.target.value = '';
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processExcelFile(e.dataTransfer.files[0]);
        }
    };

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-2xl p-4 bg-white dark:bg-gray-800 grid grid-cols-1 md:grid-cols-12 gap-4 mb-5 shadow-xs transition-all">
            {/* Left Box: Upload Excel File */}
            <div className="md:col-span-5 flex flex-col">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleFileChange}
                />
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border border-dashed rounded-xl p-5 flex flex-col items-center justify-center text-center cursor-pointer transition-colors h-full min-h-[125px] ${
                        isDragging
                            ? 'border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-emerald-500 hover:bg-gray-50/50 dark:hover:bg-gray-750'
                    }`}
                >
                    <IoCloudUploadOutline className="text-4xl text-gray-400 dark:text-gray-500 mb-1.5" />
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Upload Excel File
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Drag & drop your file here, or{' '}
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium underline">
                            click to browse
                        </span>
                    </p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                        .xlsx, .xls files only
                    </p>
                </div>
            </div>

            {/* Middle Box: Download Template */}
            <div className="md:col-span-3 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-3 md:pt-0 md:pl-4 flex flex-col justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Download Template
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                        Download the Excel template to see the required format.
                    </p>
                </div>

                <div
                    onClick={handleDownloadTemplate}
                    className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 flex items-center gap-2.5 bg-gray-50/70 hover:bg-gray-100 dark:bg-gray-700/50 dark:hover:bg-gray-700 cursor-pointer transition-colors group"
                >
                    <div className="w-6 h-6 rounded bg-[#107c41] flex items-center justify-center shrink-0 text-white shadow-xs">
                        <RiFileExcel2Fill className="text-base" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 truncate">
                        Job Prep Template.xlsx
                    </span>
                </div>
            </div>

            {/* Right Box: Last Upload Status Card */}
            <div className="md:col-span-4 bg-[#f2faf5] dark:bg-emerald-950/20 border border-[#bbf7d0] dark:border-emerald-800/40 rounded-xl p-3.5 flex flex-col justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <HiCheckCircle className="text-emerald-600 dark:text-emerald-400 text-lg shrink-0" />
                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                            Last Upload: {lastUploadInfo.time}
                        </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-medium">File:</span>{' '}
                        {lastUploadInfo.fileName}
                    </div>
                    <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                        Imported: {lastUploadInfo.total} rows • Success:{' '}
                        {lastUploadInfo.success} • Failed:{' '}
                        {lastUploadInfo.failed}
                    </div>
                </div>

                <div className="mt-2.5 flex justify-end">
                    <button
                        type="button"
                        onClick={() => setShowHistoryModal(true)}
                        className="text-xs text-emerald-700 dark:text-emerald-400 font-medium underline hover:text-emerald-800 dark:hover:text-emerald-300 cursor-pointer"
                    >
                        View Import History
                    </button>
                </div>
            </div>

            {/* Import History Modal */}
            <DefaultModal
                title="Import History"
                show={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                size="4xl"
                body={
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700 text-xs uppercase text-gray-600 dark:text-gray-300">
                                <tr>
                                    <th className="px-4 py-3">File Name</th>
                                    <th className="px-4 py-3">Uploaded Date</th>
                                    <th className="px-4 py-3">Imported</th>
                                    <th className="px-4 py-3">Success</th>
                                    <th className="px-4 py-3">Failed</th>
                                    <th className="px-4 py-3">Uploaded By</th>
                                    <th className="px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {importHistory.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-750"
                                    >
                                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                            <RiFileExcel2Fill className="text-emerald-600 text-base shrink-0" />
                                            {item.fileName}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                            {item.uploadedAt}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">
                                            {item.totalRows}
                                        </td>
                                        <td className="px-4 py-3 text-emerald-600 dark:text-emerald-400 font-medium">
                                            {item.successRows}
                                        </td>
                                        <td className="px-4 py-3 text-red-500 font-medium">
                                            {item.failedRows}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                            {item.uploadedBy}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                                {item.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                }
            />
        </div>
    );
};

export default JobPrepExcelPanel;
